import { Component } from '@theme/component';

/**
 * A minimal, purpose-built slideshow controller for the Hero Slider section.
 *
 * The theme's shared 'slideshow-component' (assets/slideshow.js) drives
 * next/previous navigation from an IntersectionObserver that watches which
 * slides are horizontally scrolled into view. Hero Slider's slides are
 * stacked directly on top of each other instead (see the section's
 * stylesheet) and slide horizontally based on the [aria-hidden] attribute
 * and a per-slide --slide-offset custom property, so every slide intersects
 * the viewport identically at all times — which breaks the shared
 * component's notion of "which slides are visible" and makes its
 * next/previous math loop back to the same slide.
 *
 * This component sidesteps that by tracking the current index directly
 * instead of deriving it from scroll position. It intentionally reuses the
 * same on:click contract (next/previous/select/pause/play) as
 * 'slideshow-component', so the existing slideshow-controls and
 * slideshow-arrows snippets work with it unchanged.
 *
 * @typedef {{ slides: HTMLElement[], dots?: HTMLElement[], current?: HTMLElement }} Refs
 * @extends {Component<Refs>}
 */
export class HeroSlider extends Component {
  requiredRefs = ['slides'];

  connectedCallback() {
    super.connectedCallback();

    this.#reset();
    this.#play();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stop();
  }

  /**
   * Advances to the next slide, wrapping around to the first.
   * @param {Event} [event]
   */
  next(event) {
    event?.preventDefault();
    this.#goTo(this.current + 1);
  }

  /**
   * Goes back to the previous slide, wrapping around to the last.
   * @param {Event} [event]
   */
  previous(event) {
    event?.preventDefault();
    this.#goTo(this.current - 1);
  }

  /**
   * Selects a specific slide by index.
   * @param {number} index
   * @param {Event} [event]
   */
  select(index, event) {
    event?.preventDefault();
    this.#goTo(Number(index));
  }

  /** Pauses autoplay. Called by the controls' pause button. */
  pause() {
    this.setAttribute('paused', '');
    this.#stop();
  }

  /** Resumes autoplay. Called by the controls' play button. */
  play() {
    this.removeAttribute('paused');
    this.#play();
  }

  /** @type {number} */
  current = 0;

  /** @type {number | undefined} */
  #interval;

  /** @param {number} index */
  #goTo(index) {
    const count = this.refs.slides?.length ?? 0;
    if (count < 2) return;

    const previousIndex = this.current;
    const nextIndex = ((index % count) + count) % count;
    if (nextIndex === previousIndex) return;

    this.current = nextIndex;
    this.#transition(previousIndex, nextIndex);
  }

  /**
   * Sets up the resting position on load: the initial slide is active
   * (centered), and every other slide waits off-screen to the right, ready
   * to slide in from the right the first time it's shown.
   */
  #reset() {
    const { slides, dots, current } = this.refs;

    slides?.forEach((slide, index) => {
      const isActive = index === this.current;
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      slide.style.setProperty('--slide-offset', isActive ? '0' : '1');
    });

    dots?.forEach((dot, index) => {
      dot.setAttribute('aria-selected', index === this.current ? 'true' : 'false');
    });

    if (current) current.textContent = `${this.current + 1}`;
  }

  /**
   * Animates from one slide to another. The incoming slide always slides in
   * from the right and the outgoing slide always slides out to the left,
   * regardless of whether this was triggered by next/previous/autoplay or a
   * dot/arbitrary selection. This keeps the motion consistent even with
   * only two slides rotating back and forth, instead of alternating
   * direction depending on which index happens to be "ahead".
   * @param {number} previousIndex
   * @param {number} nextIndex
   */
  #transition(previousIndex, nextIndex) {
    const { slides, dots, current } = this.refs;
    const outgoing = slides?.[previousIndex];
    const incoming = slides?.[nextIndex];

    if (outgoing && incoming) {
      // Force the incoming slide to its right-hand starting position first,
      // no matter where it was left after an earlier transition, and flush
      // that to the DOM before animating — otherwise the browser can
      // coalesce the reset with the animated move and skip straight to the
      // end state instead of visibly sliding in from the right.
      incoming.style.transition = 'none';
      incoming.style.setProperty('--slide-offset', '1');
      incoming.getBoundingClientRect();
      incoming.style.removeProperty('transition');

      incoming.setAttribute('aria-hidden', 'false');
      outgoing.setAttribute('aria-hidden', 'true');

      incoming.style.setProperty('--slide-offset', '0');
      outgoing.style.setProperty('--slide-offset', '-1');
    }

    dots?.forEach((dot, index) => {
      dot.setAttribute('aria-selected', index === this.current ? 'true' : 'false');
    });

    if (current) current.textContent = `${this.current + 1}`;
  }

  #play() {
    if (this.hasAttribute('paused')) return;

    const seconds = parseFloat(this.getAttribute('autoplay') || '');
    if (!seconds || Number.isNaN(seconds)) return;

    this.#stop();
    this.#interval = window.setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;
      this.next();
    }, seconds * 1000);
  }

  #stop() {
    window.clearInterval(this.#interval);
    this.#interval = undefined;
  }
}

if (!customElements.get('hero-slider-component')) {
  customElements.define('hero-slider-component', HeroSlider);
}
